import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { EngineerAvatar } from "@/components/installer/EngineerAvatar";
import { Calendar, Clock, User, Tag, Eye, EyeOff, X, Plus, Check, Save } from "lucide-react";
import { toast } from "sonner";
import TimeTextField from "@/components/forms/TimeTextField";
import { combineLocalDateTimeToIso, parseTimeInput } from "@/lib/timeInput";

const APPOINTMENT_TYPES = [
    "Sales visit", "Site survey", "Design walkthrough", "Scaffolding",
    "Installation", "Commissioning", "Handover", "Aftercare", "Other",
];

function pad2(n) { return String(n).padStart(2, "0"); }
function todayISO() {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function nextQuarterHour() {
    const d = new Date();
    const m = d.getMinutes();
    const nextM = Math.min(45, Math.ceil((m + 1) / 15) * 15);
    return `${pad2(d.getHours())}:${pad2(nextM % 60)}`;
}
/** Add `hours` to a `HH:MM` string, clamping to 23:59. Wraps intelligently
 *  so 23:30 + 1h → 23:59 rather than rolling into the next day. */
function addHoursHHMM(hhmm, hours) {
    const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
    if (!m) return hhmm;
    const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + hours * 60;
    const clamped = Math.min(total, 23 * 60 + 59);
    return `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`;
}
/** Parse a stored arrival_window ("10:00 - 11:00", "10:00 – 11:00" or legacy
 *  "10:00") into {start, end}. Falls back to a sensible 1-hour default. */
function splitWindow(win, fallbackStart) {
    const s = (win || "").trim();
    const m = s.match(/^(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})$/);
    if (m) return { start: pad2Time(m[1]), end: pad2Time(m[2]) };
    const single = s.match(/^(\d{1,2}:\d{2})$/);
    if (single) {
        const start = pad2Time(single[1]);
        return { start, end: addHoursHHMM(start, 1) };
    }
    const start = fallbackStart || nextQuarterHour();
    return { start, end: addHoursHHMM(start, 1) };
}
function pad2Time(hhmm) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
    return m ? `${pad2(m[1])}:${m[2]}` : hhmm;
}
function splitDateTime(iso) {
    if (!iso) return { date: todayISO(), time: nextQuarterHour() };
    const dm = iso.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})/);
    return dm ? { date: dm[1], time: `${dm[2]}:${dm[3]}` } : { date: todayISO(), time: nextQuarterHour() };
}

/**
 * AppointmentForm — 1:1 with mobile `src/components/AppointmentForm.tsx`.
 * Real date + time pickers, engineer picker with inline creation, appointment
 * type selector, visibility toggle, customer + internal notes.
 */
export default function AppointmentForm({ open, onClose, projectId, initial, onSaved }) {
    const isEdit = Boolean(initial?.id);
    const initSplit = useMemo(() => splitDateTime(initial?.scheduled_at), [initial?.scheduled_at]);

    const [title, setTitle] = useState("");
    const [date, setDate] = useState(todayISO());
    const [endDate, setEndDate] = useState(todayISO());
    const [time, setTime] = useState(nextQuarterHour());
    const [endTime, setEndTime] = useState(addHoursHHMM(nextQuarterHour(), 1));
    const [timeErr, setTimeErr] = useState("");
    const [endTimeErr, setEndTimeErr] = useState("");
    const [apptType, setApptType] = useState("");
    const [engineerId, setEngineerId] = useState(null);
    const [engineerName, setEngineerName] = useState("");
    const [visibility, setVisibility] = useState("customer_visible");
    const [customerInstructions, setCustomerInstructions] = useState("");
    const [internalNotes, setInternalNotes] = useState("");

    const [engineers, setEngineers] = useState([]);
    const [engineerPicker, setEngineerPicker] = useState(false);
    const [addingEng, setAddingEng] = useState(false);
    const [newEng, setNewEng] = useState({ firstName: "", lastName: "", email: "", mobileNumber: "", role: "Installer" });
    const [engSaving, setEngSaving] = useState(false);
    const [engQuery, setEngQuery] = useState("");

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTitle(initial?.title || "");
        setDate(initSplit.date);
        // APPOINTMENT-TIME-ENTRY · Hydrate END date from `end_at` when
        // present (cross-day appointments) — falls back to the start
        // date so single-day visits still line up.
        const initEnd = initial?.end_at ? splitDateTime(initial.end_at) : null;
        setEndDate(initEnd?.date || initSplit.date);
        // Hydrate the arrival range. Priority order:
        //   1) structured arrival_start/arrival_end fields (new schema)
        //   2) formatted arrival_window string (e.g. "10:00 - 11:00")
        //   3) fall back to the scheduled_at time + 60 min
        const startFromInitial =
            initial?.arrival_start ||
            splitWindow(initial?.arrival_window, initSplit.time).start ||
            initSplit.time;
        const endFromInitial =
            initial?.arrival_end ||
            splitWindow(initial?.arrival_window, initSplit.time).end ||
            addHoursHHMM(initSplit.time, 1);
        setTime(startFromInitial);
        setEndTime(endFromInitial);
        setTimeErr("");
        setEndTimeErr("");
        setApptType(initial?.appointment_type || "");
        setEngineerId(initial?.engineer_id || null);
        setEngineerName(initial?.engineer_name || "");
        setVisibility(initial?.visibility || "customer_visible");
        setCustomerInstructions(initial?.customer_instructions || "");
        setInternalNotes(initial?.internal_notes || "");
        setErrors({});
        api.listEngineers().then(setEngineers).catch(() => setEngineers([]));
    }, [open, initial, initSplit]);

    const filtered = useMemo(() => {
        const q = engQuery.trim().toLowerCase();
        return q ? engineers.filter(e => (e.displayName || "").toLowerCase().includes(q)) : engineers;
    }, [engineers, engQuery]);

    const validate = () => {
        const e = {};
        if (!title.trim()) e.title = "Title is required";
        if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) e.date = "Valid start date required";
        if (!endDate || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) e.endDate = "Valid end date required";
        const startCanonical = parseTimeInput(time);
        const endCanonical = parseTimeInput(endTime);
        if (!startCanonical) e.time = "Valid start time required (HH:MM)";
        if (!endCanonical) e.endTime = "Valid end time required (HH:MM)";
        if (startCanonical && endCanonical && date && endDate) {
            const startIso = combineLocalDateTimeToIso(date, startCanonical);
            const endIso = combineLocalDateTimeToIso(endDate, endCanonical);
            if (startIso && endIso && new Date(endIso) <= new Date(startIso)) {
                e.endTime = "End must be after start";
            }
        }
        if (!apptType) e.apptType = "Appointment type is required";
        setErrors(e);
        return !Object.keys(e).length;
    };

    /** Keep the "To" time exactly 60 min after "From" whenever the installer
     *  hasn't manually edited it out of that default. */
    const onStartChange = (newStart) => {
        const canonical = parseTimeInput(newStart);
        // Only auto-track when both the incoming start AND the current
        // end are parseable; otherwise leave `endTime` alone so the
        // user's manual value survives keystroke-level updates.
        const startCanonical = canonical || newStart;
        const currentEndCanonical = parseTimeInput(endTime);
        const wasDefault = canonical && currentEndCanonical
            && currentEndCanonical === addHoursHHMM(canonical, 1);
        setTime(startCanonical);
        if (canonical) {
            if (wasDefault || !currentEndCanonical || currentEndCanonical <= canonical) {
                setEndTime(addHoursHHMM(canonical, 1));
            }
        }
    };

    const submit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const startCanonical = parseTimeInput(time);
            const endCanonical = parseTimeInput(endTime);
            const startIso = combineLocalDateTimeToIso(date, startCanonical);
            const endIso = combineLocalDateTimeToIso(endDate, endCanonical);
            const body = {
                title: title.trim(),
                engineer_name: engineerName || "Unassigned",
                arrival_window: `${startCanonical} - ${endCanonical}`,
                arrival_start: startCanonical,
                arrival_end: endCanonical,
                appointment_type: apptType,
                scheduled_at: `${date}T${startCanonical}:00Z`,
                // APPOINTMENT-TIME-ENTRY · Send explicit ISO
                // start_at / end_at so cross-day visits (endDate !=
                // date) survive the backend contract without the
                // synthesiser collapsing them onto scheduled_at.
                start_at: startIso,
                end_at: endIso,
                visibility,
                customer_instructions: customerInstructions.trim(),
                internal_notes: internalNotes.trim(),
            };
            if (engineerId) body.engineer_id = engineerId;
            let saved;
            if (isEdit) {
                saved = await api.updateAppointment(initial.id, body);
                onSaved?.(saved, "Appointment updated");
            } else {
                body.status = "scheduled";
                saved = await api.createAppointment(projectId, body);
                onSaved?.(saved, "Appointment saved");
            }
            onClose();
        } catch (err) {
            setErrors({ form: formatApiError(err.response?.data?.detail) || err.message });
        } finally { setSaving(false); }
    };

    const addEngineer = async () => {
        const first = newEng.firstName.trim();
        const last = newEng.lastName.trim();
        const emailVal = newEng.email.trim();
        if (!first || !last || !emailVal) return;
        setEngSaving(true);
        try {
            const created = await api.createEngineer({
                firstName: first,
                lastName: last,
                email: emailVal,
                mobileNumber: newEng.mobileNumber.trim() || undefined,
                role: newEng.role || "Installer",
            });
            setEngineers(prev => [...prev, created].sort((a, b) => a.displayName.localeCompare(b.displayName)));
            setEngineerId(created.id);
            setEngineerName(created.displayName);
            setAddingEng(false);
            setNewEng({ firstName: "", lastName: "", email: "", mobileNumber: "", role: "Installer" });
            toast.success("Engineer added");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to add engineer");
        } finally { setEngSaving(false); }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center px-4" data-testid="appointment-form">
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl">
                <div className="px-6 py-5 border-b border-[#E5E7EB] flex items-start justify-between">
                    <div>
                        <div className="text-xl text-[#131B2F] font-medium">{isEdit ? "Edit visit" : "Book a visit"}</div>
                        <div className="text-sm text-[#4B5565] mt-0.5">
                            {isEdit ? "Update details and re-notify the customer if needed." : "Select a date, engineer and share notes."}
                        </div>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]" aria-label="Close">
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <Field label="Title" error={errors.title}>
                        <input
                            data-testid="af-title" value={title} onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Installation Day 1"
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Start date" error={errors.date} icon={Calendar}>
                            <input
                                data-testid="af-date" type="date" value={date} min={todayISO()}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    // Keep endDate ≥ start date when the user
                                    // hasn't manually pushed it out.
                                    if (!endDate || endDate < v) setEndDate(v);
                                    setDate(v);
                                }}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </Field>
                        <Field label="End date" error={errors.endDate} icon={Calendar}>
                            <input
                                data-testid="af-end-date" type="date" value={endDate} min={date || todayISO()}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </Field>
                    </div>
                    <Field label="Arrival window" error={errors.time || errors.endTime || timeErr || endTimeErr} icon={Clock}>
                        <div className="flex items-center gap-2" data-testid="af-arrival-window">
                            <TimeTextField
                                value={time}
                                onChange={(canonical, err) => {
                                    onStartChange(canonical || "");
                                    setTimeErr(err || "");
                                }}
                                error={timeErr}
                                ariaLabel="Arrival window start"
                                testid="af-time"
                                className="flex-1 min-w-0 h-11 rounded-md border border-[#E5E7EB] bg-white px-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                            <span className="text-[#4B5565] text-sm shrink-0">to</span>
                            <TimeTextField
                                value={endTime}
                                onChange={(canonical, err) => {
                                    setEndTime(canonical);
                                    setEndTimeErr(err || "");
                                }}
                                error={endTimeErr}
                                ariaLabel="Arrival window end"
                                testid="af-time-end"
                                className="flex-1 min-w-0 h-11 rounded-md border border-[#E5E7EB] bg-white px-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </div>
                    </Field>

                    <Field label="Appointment type" error={errors.apptType} icon={Tag}>
                        <select
                            data-testid="af-type" value={apptType} onChange={(e) => setApptType(e.target.value)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        >
                            <option value="">Choose type…</option>
                            {APPOINTMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </Field>

                    <Field label="Engineer" icon={User}>
                        <button
                            type="button" data-testid="af-engineer-picker"
                            onClick={() => setEngineerPicker(true)}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-left text-base text-[#131B2F] hover:border-[#3E9E2E] transition-colors"
                        >
                            {engineerName || <span className="text-[#4B5565]">Assign an engineer…</span>}
                        </button>
                    </Field>

                    <Field label="Visibility">
                        <div className="grid grid-cols-2 gap-2">
                            <VisBtn active={visibility === "customer_visible"} icon={Eye} label="Customer visible"
                                onClick={() => setVisibility("customer_visible")} testid="af-vis-customer" />
                            <VisBtn active={visibility === "internal_only"} icon={EyeOff} label="Internal only"
                                onClick={() => setVisibility("internal_only")} testid="af-vis-internal" />
                        </div>
                    </Field>

                    {visibility === "customer_visible" && (
                        <Field label="Customer instructions">
                            <textarea
                                data-testid="af-cust-notes" rows={3}
                                value={customerInstructions} onChange={(e) => setCustomerInstructions(e.target.value)}
                                placeholder="e.g. Please leave driveway clear from 7am."
                                className="w-full rounded-md border border-[#E5E7EB] bg-white p-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                            />
                        </Field>
                    )}

                    <Field label="Internal notes">
                        <textarea
                            data-testid="af-int-notes" rows={2}
                            value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)}
                            placeholder="Only visible to your team"
                            className="w-full rounded-md border border-[#E5E7EB] bg-white p-3 text-base text-[#131B2F] outline-none focus:border-[#3E9E2E]"
                        />
                    </Field>

                    {!!errors.form && <div className="text-sm text-[#E53E3E]">{errors.form}</div>}
                </div>

                <div className="px-6 py-4 border-t border-[#E5E7EB] flex gap-2">
                    <button className="pe-btn-outline flex-1 justify-center h-11" onClick={onClose}>Cancel</button>
                    <button data-testid="af-submit" onClick={submit} disabled={saving} className="pe-btn-primary flex-1 justify-center h-11 disabled:opacity-60">
                        {saving ? "Saving…" : (<><Save size={14} strokeWidth={2} /> {isEdit ? "Save changes" : "Book visit"}</>)}
                    </button>
                </div>
            </div>

            {/* Engineer picker overlay */}
            {engineerPicker && (
                <div className="fixed inset-0 bg-black/55 flex items-center justify-center px-4 z-10" onClick={() => setEngineerPicker(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="engineer-picker">
                        <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between">
                            <div className="text-lg text-[#131B2F] font-medium">Assign engineer</div>
                            <button onClick={() => setEngineerPicker(false)} className="w-9 h-9 rounded-full bg-[#F5F5F7] flex items-center justify-center text-[#4B5565]"><X size={14} /></button>
                        </div>
                        {addingEng ? (
                            <div className="p-4 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <input placeholder="First name" value={newEng.firstName} onChange={(e) => setNewEng({ ...newEng, firstName: e.target.value })}
                                        data-testid="eng-new-first"
                                        className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                                    <input placeholder="Last name" value={newEng.lastName} onChange={(e) => setNewEng({ ...newEng, lastName: e.target.value })}
                                        data-testid="eng-new-last"
                                        className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                                </div>
                                <input placeholder="Email" type="email" value={newEng.email} onChange={(e) => setNewEng({ ...newEng, email: e.target.value })}
                                    data-testid="eng-new-email"
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                                <input placeholder="Mobile (optional)" value={newEng.mobileNumber} onChange={(e) => setNewEng({ ...newEng, mobileNumber: e.target.value })}
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3.5 text-base outline-none focus:border-[#3E9E2E]" />
                                <select value={newEng.role} onChange={(e) => setNewEng({ ...newEng, role: e.target.value })}
                                    data-testid="eng-new-role"
                                    className="w-full h-11 rounded-md border border-[#E5E7EB] px-3 text-base bg-white outline-none focus:border-[#3E9E2E]">
                                    {["Lead Installer","Installer","Electrician","Surveyor","Project Manager","Office"].map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                <div className="flex gap-2 pt-2">
                                    <button className="pe-btn-outline flex-1 justify-center h-11" onClick={() => setAddingEng(false)}>Cancel</button>
                                    <button data-testid="eng-new-save" onClick={addEngineer}
                                            disabled={engSaving || !newEng.firstName.trim() || !newEng.lastName.trim() || !newEng.email.trim()}
                                            className="pe-btn-primary flex-1 justify-center h-11 disabled:opacity-60">
                                        {engSaving ? "Adding…" : "Add engineer"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="p-4 pb-2">
                                    <input placeholder="Search engineers…" value={engQuery} onChange={(e) => setEngQuery(e.target.value)}
                                        className="w-full h-10 rounded-md border border-[#E5E7EB] px-3 text-base outline-none focus:border-[#3E9E2E]" />
                                </div>
                                <div className="flex-1 overflow-y-auto px-2 pb-2">
                                    {filtered.length === 0 && (
                                        <div className="px-3 py-6 text-center text-sm text-[#4B5565]">No engineers match.</div>
                                    )}
                                    {filtered.map((e) => (
                                        <button key={e.id}
                                            data-testid={`eng-opt-${e.id}`}
                                            onClick={() => { setEngineerId(e.id); setEngineerName(e.displayName); setEngineerPicker(false); }}
                                            className="w-full flex items-center gap-3 px-3 py-3 rounded-md hover:bg-[#F5F5F7] transition-colors text-left"
                                        >
                                            <EngineerAvatar eng={e} size={36} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-base text-[#131B2F] font-medium truncate">{e.displayName}</div>
                                                <div className="text-xs text-[#4B5565] truncate">{e.role}{e.email ? ` · ${e.email}` : ""}</div>
                                            </div>
                                            {engineerId === e.id && <Check size={16} className="text-[#3E9E2E]" />}
                                        </button>
                                    ))}
                                </div>
                                <div className="p-3 border-t border-[#E5E7EB]">
                                    <button data-testid="eng-add-toggle" onClick={() => setAddingEng(true)} className="w-full pe-btn-outline justify-center h-11">
                                        <Plus size={14} strokeWidth={2} /> Add new engineer
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, icon: Icon, error, children }) {
    return (
        <div>
            <label className="flex items-center gap-1.5 text-sm text-[#4B5565] mb-1.5">
                {Icon && <Icon size={12} strokeWidth={1.75} />}
                {label}
            </label>
            {children}
            {error && <div className="mt-1 text-xs text-[#E53E3E]">{error}</div>}
        </div>
    );
}

function VisBtn({ active, icon: Icon, label, onClick, testid }) {
    return (
        <button
            type="button" onClick={onClick} data-testid={testid}
            className={`h-11 rounded-md border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                active ? "border-[#3E9E2E] bg-[#F1F8EC] text-[#2E7E22]" : "border-[#E5E7EB] bg-white text-[#4B5565] hover:bg-[#F5F5F7]"
            }`}
        >
            <Icon size={13} strokeWidth={1.75} /> {label}
        </button>
    );
}
